import React, { useState, useEffect } from "react";

export default function TopicExplainer({ topic }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    setStep(0);
    const timers = [400, 1000, 1800, 2600].map((d, i) =>
      setTimeout(() => setStep(i + 1), d)
    );
    return () => timers.forEach(clearTimeout);
  }, [topic]);

  const style = {
    wrap: { background: "#111318", border: "1px solid #1E2433", borderRadius: 14, padding: 14, marginTop: 8 },
    title: { fontSize: 11, fontWeight: 700, color: "#F59E0B", marginBottom: 10, letterSpacing: 0.5 },
    step: (visible) => ({
      fontSize: 12, color: "#CBD5E1", marginBottom: 6,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateX(0)" : "translateX(-8px)",
      transition: "all 0.4s ease",
    }),
  };

  if (topic === "fractions") return (
    <div style={style.wrap}>
      <div style={style.title}>{"\uD83C\uDF55"} Fractions {"\u2014"} How They Work</div>
      <svg width="120" height="120" viewBox="0 0 120 120" style={{ display: "block", margin: "0 auto 10px" }}>
        {[0, 90, 180, 270].map((angle, i) => {
          const r1 = angle * Math.PI / 180, r2 = (angle + 90) * Math.PI / 180;
          const x1 = 60 + 50 * Math.cos(r1), y1 = 60 + 50 * Math.sin(r1);
          const x2 = 60 + 50 * Math.cos(r2), y2 = 60 + 50 * Math.sin(r2);
          const cols = ["#EF4444", "#F59E0B", "#10B981", "#3B82F6"];
          return (
            <path key={i} d={`M60 60 L${x1} ${y1} A50 50 0 0 1 ${x2} ${y2}Z`}
              fill={step >= 3 && i === 0 ? "#F59E0B" : cols[i]}
              stroke="#05070D" strokeWidth="2.5"
              style={{ opacity: step >= 1 ? 1 : 0, transformOrigin: "60px 60px",
                transform: step >= 3 && i === 0 ? "scale(1.07)" : "scale(1)",
                transition: `all 0.4s ease ${i * 0.1}s` }} />
          );
        })}
        <circle cx="60" cy="60" r="5" fill="#05070D" />
      </svg>
      <div style={style.step(step >= 1)}>{"\uD83D\uDD2A"} Cut into 4 <strong>equal</strong> pieces</div>
      <div style={style.step(step >= 2)}>{"\uD83D\uDCCA"} Bottom number = total pieces</div>
      <div style={style.step(step >= 3)}>{"\u2B50"} Top number = pieces you have</div>
      <div style={style.step(step >= 4)}>{"\u270F\uFE0F"} 1 out of 4 = <strong style={{ color: "#F59E0B" }}>1/4</strong></div>
    </div>
  );

  if (topic === "placevalue") return (
    <div style={style.wrap}>
      <div style={style.title}>{"\uD83D\uDD22"} Place Value {"\u2014"} 347</div>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 10 }}>
        {[{ d: "3", l: "Hundreds", v: "300", c: "#3B82F6" },
          { d: "4", l: "Tens",     v: "40",  c: "#8B5CF6" },
          { d: "7", l: "Ones",     v: "7",   c: "#10B981" }].map((card, i) => (
          <div key={i} style={{
            background: card.c + "22", border: `2px solid ${card.c}`,
            borderRadius: 10, padding: "8px 12px", textAlign: "center",
            opacity: step >= i + 1 ? 1 : 0,
            transform: step >= i + 1 ? "translateY(0)" : "translateY(12px)",
            transition: `all 0.4s ease ${i * 0.15}s`,
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: card.c }}>{card.d}</div>
            <div style={{ fontSize: 9, color: "#94A3B8" }}>{card.l}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: card.c }}>= {card.v}</div>
          </div>
        ))}
      </div>
      {step >= 4 && (
        <div style={{ textAlign: "center", fontSize: 18, fontWeight: 700 }}>
          <span style={{ color: "#3B82F6" }}>300</span>+
          <span style={{ color: "#8B5CF6" }}>40</span>+
          <span style={{ color: "#10B981" }}>7</span>=
          <span style={{ color: "#F59E0B" }}>347</span>
        </div>
      )}
    </div>
  );

  if (topic === "inference") return (
    <div style={style.wrap}>
      <div style={style.title}>{"\uD83D\uDD0D"} Inference {"\u2014"} Be a Detective</div>
      {["\uD83C\uDF27\uFE0F Wearing a raincoat", "\u2602\uFE0F Carrying an umbrella", "\uD83D\uDCA6 Ground is wet"].map((clue, i) => (
        <div key={i} style={{
          ...style.step(step >= i + 1),
          background: "#0A0C10", border: "1px solid #1E2433",
          borderRadius: 8, padding: "7px 10px", marginBottom: 6,
        }}>{clue}</div>
      ))}
      {step >= 4 && (
        <div style={{ textAlign: "center", background: "#8B5CF620", border: "1px solid #8B5CF6",
          borderRadius: 10, padding: 10, fontSize: 13, fontWeight: 700, color: "#8B5CF6" }}>
          {"\uD83D\uDCA1"} We INFER {"\u2192"} it must be raining!
        </div>
      )}
    </div>
  );

  if (topic === "multiplication") return (
    <div style={style.wrap}>
      <div style={style.title}>{"\u2716\uFE0F"} Multiplication = Groups Of</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", marginBottom: 10 }}>
        {["#3B82F6", "#8B5CF6", "#10B981"].map((col, r) => (
          <div key={r} style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {[0, 1, 2, 3].map(c => (
              <div key={c} style={{
                width: 24, height: 24, borderRadius: "50%", background: col,
                transform: step >= 1 ? "scale(1)" : "scale(0)",
                transition: `all 0.25s ease ${(r * 4 + c) * 0.05}s`,
              }} />
            ))}
            {step >= 2 && <span style={{ color: col, fontWeight: 600, fontSize: 12 }}>= 4</span>}
          </div>
        ))}
      </div>
      {step >= 3 && (
        <div style={{ textAlign: "center", fontSize: 16, fontWeight: 700 }}>
          <span style={{ color: "#3B82F6" }}>4</span>+
          <span style={{ color: "#8B5CF6" }}>4</span>+
          <span style={{ color: "#10B981" }}>4</span>=
          <span style={{ color: "#F59E0B" }}>12 {"\u2192"} 3{"\u00D7"}4=12</span>
        </div>
      )}
    </div>
  );


  return null;
}
